import { AfterViewInit, Component, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { isHidCredentialRedemptionResponseProviderParams, isLegicCredentialRedemptionResponseProviderParams, isWavelynxCredentialRedemptionResponseProviderParams } from '~api/helpers/types';
import { ErrorModel } from '~api/models/error.model';
import { CredentialService } from '~api/services/credential.service';
import { Redirect } from '~app/provisioning/redirect/redirect';
import { CredentialInfo } from '~app/types/credential-info';
import { RedemptionInfo } from '~app/types/redemption-info';
import { CredentialRedemptionType } from '~generated-api/models/credential-redemption-type';

@Component({
  selector: 'app-platform-ios',
  imports: [
    TranslateModule,
    Redirect
  ],
  templateUrl: './ios.html',
  styleUrl: './ios.css'
})
export class Ios implements AfterViewInit {
  readonly redemptionInfo = input.required<RedemptionInfo>();
  readonly credentialInfo = input.required<CredentialInfo>();
  readonly providerError = output<string>();
  protected redirectTo: string | undefined = undefined;
  private readonly credential = inject(CredentialService);

  ngAfterViewInit(): void {
    if (this.credentialInfo().provider === 'WAVELYNX') {
      this.credential.confirmCredentialRedemption(this.redemptionInfo().id!, CredentialRedemptionType.WALLET_IOS).subscribe(result => {
        if (result instanceof ErrorModel) {
          this.providerError.emit('cant get weblink');
        } else {
          try {
            const deepLink = isWavelynxCredentialRedemptionResponseProviderParams(result.providerParams) ? result.providerParams.webLink : null;
            if (deepLink) {
              this.redirectTo = deepLink;
              return;
            }
          } catch (e) {
          }
          this.providerError.emit('cant get/parse weblink');
        }
      });
    } else {
      const infoResult = this.redemptionInfo()?.result;
      if (!infoResult) {
        this.providerError.emit('cant get apple info result');
        return;
      }
      const infoProviderParams = ('providerParams' in infoResult) ? infoResult['providerParams'] : undefined;
      if (!infoProviderParams) {
        this.providerError.emit('cant get apple info provider params');
        return;
      }
      let partnerId: string | undefined;
      let domain: string | undefined;

      if (this.credentialInfo().provider === 'LEGIC_CONNECT' && isLegicCredentialRedemptionResponseProviderParams(infoProviderParams)) {
        partnerId = infoProviderParams.applePartner;
        domain = infoProviderParams.appleDomain;
      } else if (this.credentialInfo().provider === 'HID' && isHidCredentialRedemptionResponseProviderParams(infoProviderParams)) {
        partnerId = infoProviderParams.applePartner;
        domain = infoProviderParams.appleDomain;
      }

      if (!domain || !partnerId) {
        this.providerError.emit('cant get apple info provider param values');
        return;
      }

      initAddToAppleWallet({
        partnerId: partnerId,
        domain: domain,
        buttonId: 'appleButton',
        jwtResolver: async () => {
          const result = await firstValueFrom(this.credential.confirmCredentialRedemption(this.redemptionInfo().id!, CredentialRedemptionType.WALLET_IOS));
          const providerParams = (result && 'providerParams' in result) ? result['providerParams'] : undefined;
          if (!providerParams) {
            this.providerError.emit('no providerParams in confirmCredentialRedemption() response');
            return null;
          }
          let tokenRaw: string | undefined;
          if (this.credentialInfo().provider === 'LEGIC_CONNECT' && isLegicCredentialRedemptionResponseProviderParams(providerParams)) {
            tokenRaw = providerParams.appleToken;
          } else if (this.credentialInfo().provider === 'HID' && isHidCredentialRedemptionResponseProviderParams(providerParams)) {
            tokenRaw = providerParams.appleToken;
          }
          if (!tokenRaw) {
            this.providerError.emit('empty apple token in confirmCredentialRedemption() response');
            return null;
          }
          try {
            return JSON.parse(tokenRaw);
          } catch (e) {
            this.providerError.emit('cant parse apple token');
            return null;
          }
        },
        resultResolver: async (msg) => {
          if (['200', '206'].includes(msg.status)) {
            return;
          }
          if (['202', '201', '408', '500'].includes(msg.status) || parseInt(msg.status) >= 400) {
            this.providerError.emit('apple error response: ' + msg.status + ' - ' + msg.statusMessage);
          }
        },
      });
    }
  }

}
